import type { StatusChipFamilyKey } from 'utils/badgeColors';

export interface LegendItemDefinition {
  key: string;
  family: StatusChipFamilyKey;
  label: string;
  description: string;
}

export interface LegendGroupDefinition {
  key: string;
  icon: 'eye' | 'clock';
  title: string;
  chip?: string;
  note: string;
  noAction: string;
  respond: boolean;
  items: readonly LegendItemDefinition[];
}

export interface LegendStageDefinition {
  key: string;
  number: number;
  family: StatusChipFamilyKey;
  title: string;
  note: string;
  items: readonly LegendItemDefinition[];
  group?: LegendGroupDefinition;
}

export const providerClaimLegendStages: readonly LegendStageDefinition[] = [
  {
    key: 'biller',
    number: 1,
    family: 'teal',
    title: 'Before it goes out, and when it comes back',
    note: 'Either not sent yet, or back from the payor and with QBilling for review. Your biller builds the claim from your daysheet, checks it, and works through anything the payor sends back.',
    items: [
      {
        key: 'draft',
        family: 'teal',
        label: 'Draft',
        description:
          'Claim has been created but not ready to bill. No action unless the biller reaches out to you.',
      },
    ],
    group: {
      key: 'withYourBiller',
      icon: 'eye',
      respond: true,
      title: 'With QBilling',
      note: 'These are on QBilling’s desk — either waiting on a final check before they go out, or back from the payor with a decision to make. No action unless the biller reaches out to you.',
      noAction:
        'Nothing to do here. The only time we need you is when a claim is flagged',
      items: [
        {
          key: 'rejected',
          family: 'amber',
          label: 'Rejected',
          description:
            'The payor refused the tariff. Your biller is reading the EOB (explanation of benefits) and will either correct and resubmit, query for payment, invoice a third party, or, if deemed correct, accept the refusal and mark it as Unclaimable.',
        },
        {
          key: 'paidShort',
          family: 'amber',
          label: 'Paid Short',
          description:
            'Assessed by the payor and paid below what was billed. The amount received has been paid to you; the difference stays in Outstanding until your biller reviews it and either accepts it or queries it. If other tariffs on the claim are still pending, we may be waiting on those to come back assessed before this one reaches its final state.',
        },
        {
          key: 'writeOffPending',
          family: 'amber',
          label: 'Write-off Pending',
          description:
            'Your biller is waiting to hear back from the payor or the patient. It is a holding state before a write-off, and these are usually more than six months old.',
        },
        {
          key: 'readyToSubmit',
          family: 'teal',
          label: 'Ready to Submit',
          description:
            'We have all the details — waiting on a final check before submission to the payor.',
        },
      ],
    },
  },
  {
    key: 'payor',
    number: 2,
    family: 'purple',
    title: 'With the Payor',
    note: "It has left our hands. Timing here follows the payor's cycle, not ours.",
    items: [
      {
        key: 'uninsured',
        family: 'amber',
        label: 'Uninsured',
        description:
          'The patient was not covered on the date of service. Your biller is moving the amount to a direct invoice to either the patient or a third party (Blue Cross, WCB, etc.), so it stays outstanding until that invoice is paid.',
      },
      {
        key: 'payorInvoiced',
        family: 'purple',
        label: 'Payor Invoiced',
        description:
          'Not a public-insurance claim — invoiced directly to a third party, insurer, or the patient. Waiting on that invoice to be paid.',
      },
    ],
    group: {
      key: 'waitingOnPayor',
      icon: 'clock',
      respond: false,
      title: 'Waiting on the public payor',
      chip: 'No action possible',
      note: 'These are with the public payor and cannot be worked on until the payor responds with an assessment.',
      noAction:
        'Nothing to do, and nothing your biller can do until these come back assessed.',
      items: [
        {
          key: 'submitted',
          family: 'purple',
          label: 'Submitted',
          description:
            'Included in a submission file and sent to the public payor. Waiting for assessment.',
        },
        {
          key: 'resubmitted',
          family: 'purple',
          label: 'Resubmitted',
          description:
            'Assessed and refused at least once, corrected by your biller, and sent back to the payor. If the tariff value changed, the difference shows in the Adjustments column with your biller’s reason beside it.',
        },
        {
          key: 'pending',
          family: 'purple',
          label: 'Pending',
          description:
            'Held by the public payor under EOB 77 awaiting assessment. Nothing can be done until it is assessed, which can take 1-6 months. Interest is typically added when paid.',
        },
        {
          key: 'queried',
          family: 'purple',
          label: 'Queried',
          description:
            'The payor refused the tariff, your biller disagrees with the assessment, and a query is in with the payor. It updates once the payor responds.',
        },
      ],
    },
  },
  {
    key: 'settled',
    number: 3,
    family: 'amber',
    title: 'Settled',
    note: 'Nothing further will happen. These no longer count as outstanding.',
    items: [
      {
        key: 'paid',
        family: 'green',
        label: 'Paid',
        description: 'Money received on the remittance for this tariff.',
      },
      {
        key: 'adjusted',
        family: 'gray',
        label: 'Adjusted',
        description:
          'The payor’s refusal was reviewed and accepted as correct, so this tariff was adjusted and was replaced with another tariff and resubmitted. It closes at its original value; the difference between the two tariffs is recorded in your Adjustments column.',
      },
      {
        key: 'writtenOff',
        family: 'gray',
        label: 'Written Off',
        description:
          'Payment could not be collected. These claims may be used for write-off purposes.',
      },
      {
        key: 'unclaimable',
        family: 'gray',
        label: 'Unclaimable',
        description:
          'This tariff cannot be billed to MB Health or another payor. Your biller has reviewed the payor’s response and confirmed the reason — check the biller’s comment on this tariff or the explanation of benefits (EOB) for details.',
      },
    ],
  },
];

export const providerClaimLegendCopy = {
  workflowLabel: 'Biller workflow',
  workflowTooltip:
    'Where your biller has each tariff. For your visibility — nothing here is yours to action.',
  open: 'What do these mean?',
  close: 'Close legend',
  title: 'Tariff Status Legend',
  overviewClaim:
    'A claim is the bill for a patient visit. It can contain one or more tariffs — the individual services being billed.',
  overviewTariffs:
    'Each tariff is assessed separately by the payor. This means tariffs on the same claim can be at different stages at the same time — for example, one may be paid while another is still under review or needs correction.',
  visibilityTitle: 'These statuses are for your visibility.',
  mixedLabel: 'Mixed',
  mixedDescription:
    'A claim shows as Mixed when its tariffs are not all at the same stage. The number beside it is how many different statuses are on the claim. Click the claim row, or the arrow on the far left, to see each tariff within the claim and where it sits.',
  footer:
    'Tabs filter tariffs, not whole claims. A claim appears in Closed as soon as any one of its tariffs is Paid, Written off, or confirmed unclaimable — and it can appear in more than one tab at the same time. The row shows only the tariffs that match the tab you are in; expand it to see the rest.',
  search: 'Search a tariff status',
  clearSearch: 'Clear search',
  noMatch: 'No tariff status matches “{{query}}”.',
  visibilityBeforeRespond:
    'QBilling drives every stage below. There is nothing for you to do unless a claim shows a',
  visibilityBeforeFilter:
    'flag — those are collected in the alert at the top of the page, or click the',
  visibilityAfterFilter: 'filter.',
  respond: 'Respond',
  needsYou: 'Needs you',
};
